
import { defineComponent } from 'vue';

const TEMPLATE_VIEWPORT_WIDTH = 3840;
const TEMPLATE_VIEWPORT_HEIGHT = 2160;

export default defineComponent({
    name: 'LiveTemplatePreview',
    props: {
        layoutType: { type: String, required: true },
        layoutData: { type: Object, required: true }
    },
    data: () => ({
        scale: 0.2,
        frameLoaded: false,
        ready: false,
        readyMessageHandler: null as any,
        readyFallbackTimer: null as any,
        // Cache-buster: after a portal deploy an open tab can pair a new
        // parent with a cached pre-handshake template document (or vice
        // versa), which left the loading state stuck until the cache
        // expired. A per-mount query forces the iframe to fetch the current
        // deploy's document; its hashed assets stay browser-cached.
        cacheBust: Date.now(),
        resizeObserver: null as ResizeObserver | null
    }),
    computed: {
        frameSrc(): string {
            // The 'live' id/signature route params tell the template page to
            // skip its templaterequest fetch and wait for postMessage data.
            return `/signage/template/${this.layoutType}/live/live?v=${this.cacheBust}`;
        },
        frameStyle(): Record<string, string> {
            return {
                width: `${TEMPLATE_VIEWPORT_WIDTH}px`,
                height: `${TEMPLATE_VIEWPORT_HEIGHT}px`,
                transform: `scale(${this.scale})`,
                transformOrigin: 'top left',
                border: 'none',
                pointerEvents: 'none',
                // Hidden until the template page confirms it is listening,
                // so the SPA boot inside the frame never flashes through.
                opacity: this.ready ? '1' : '0'
            };
        }
    },
    mounted() {
        this.updateScale();
        this.resizeObserver = new ResizeObserver(() => this.updateScale());
        this.resizeObserver.observe(this.$refs.wrap as Element);

        // Handshake: the iframe's 'load' event fires before the Vue app
        // inside it mounts its message listener, so data posted at load can
        // be lost (the preview then sat empty until the next edit). The
        // template page announces readiness once its listener is attached;
        // only then is the first payload guaranteed to land.
        this.readyMessageHandler = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) { return; }
            if (event.data && event.data.type === 'ditto-live-preview-ready') {
                this.frameLoaded = true;
                this.postLayoutData();
                this.ready = true;
                if (this.readyFallbackTimer) {
                    clearTimeout(this.readyFallbackTimer);
                    this.readyFallbackTimer = null;
                }
            }
        };
        window.addEventListener('message', this.readyMessageHandler);

        // The loading state must never be able to stick: if no handshake
        // arrives (version-mismatched frame document, or anything else that
        // eats the message), reveal the frame and send the data anyway. A
        // handshake-less template page still renders from this post.
        this.readyFallbackTimer = setTimeout(() => {
            if (!this.ready) {
                this.frameLoaded = true;
                this.postLayoutData();
                this.ready = true;
            }
        }, 5000);
    },
    beforeUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        if (this.readyMessageHandler) {
            window.removeEventListener('message', this.readyMessageHandler);
        }
        if (this.readyFallbackTimer) {
            clearTimeout(this.readyFallbackTimer);
        }
    },
    watch: {
        layoutData: {
            handler() {
                this.postLayoutData();
            },
            deep: true
        }
    },
    methods: {
        onFrameLoad() {
            // Belt and braces alongside the readiness handshake: if the
            // template page mounted faster than 'load' fired, this delivers
            // the first payload; otherwise the handshake will.
            this.frameLoaded = true;
            this.postLayoutData();
        },
        postLayoutData() {
            if (!this.frameLoaded) { return; }
            const frame = this.$refs.frame as HTMLIFrameElement;
            if (frame && frame.contentWindow) {
                // Same-origin only: the template routes are unauthenticated
                // (the server-side renderer needs them to be), so the page
                // accepts data exclusively from its own origin.
                frame.contentWindow.postMessage({
                    type: 'ditto-live-preview',
                    layoutData: JSON.parse(JSON.stringify(this.layoutData))
                }, window.location.origin);
            }
        },
        updateScale() {
            const wrap = this.$refs.wrap as HTMLElement;
            if (wrap && wrap.clientWidth > 0) {
                this.scale = wrap.clientWidth / TEMPLATE_VIEWPORT_WIDTH;
                wrap.style.height = `${TEMPLATE_VIEWPORT_HEIGHT * this.scale}px`;
            }
        }
    }
});
